/*
  bartesian-info-note.js
  Small info notes that open from an icon button (e.g. "Most capsules" -> which ones).
  Reusable: load once on any page that has [data-info-note] buttons.

  Markup contract:
  - button[data-info-note][popovertarget]   the info icon
  - [popover] element with the matching id   the note
  - optional [data-info-scroll] ancestor     a sideways-scrolling area; the note closes when it scrolls

  Behaviour:
  - Tap/click toggles the note (native popover: Escape and clicking elsewhere close it).
  - Mouse hover opens it too, with a short delay before closing so the pointer
    can move onto the note (e.g. to follow a link).
  - The note is positioned next to its icon and flips above if there's no room below.
  - Closes when a [data-info-scroll] area scrolls sideways so it never drifts away from its icon.
  - Browsers without the Popover API fall back to toggling [hidden].
*/
(function () {
  const supportsPopover = typeof HTMLElement !== 'undefined' && HTMLElement.prototype.hasOwnProperty('popover');
  const GAP = 8;
  const EDGE = 12;

  function position(button, note) {
    const b = button.getBoundingClientRect();
    const width = note.offsetWidth;
    const height = note.offsetHeight;

    let left = b.left + b.width / 2 - width / 2;
    left = Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE));

    let top = b.bottom + GAP;
    if (top + height > window.innerHeight - EDGE) top = b.top - height - GAP;

    note.style.left = `${left}px`;
    note.style.top = `${Math.max(EDGE, top)}px`;
  }

  function isOpen(note) {
    return supportsPopover ? note.matches(':popover-open') : !note.hidden;
  }

  function open(button, note) {
    if (isOpen(note)) return;
    if (supportsPopover) note.showPopover();
    else { note.hidden = false; }
    position(button, note);
    button.setAttribute('aria-expanded', 'true');
  }

  function close(button, note) {
    if (!isOpen(note)) return;
    if (supportsPopover) note.hidePopover();
    else { note.hidden = true; }
    button.setAttribute('aria-expanded', 'false');
  }

  function initInfo(button) {
    if (button.dataset.infoNoteInit === 'true') return;
    button.dataset.infoNoteInit = 'true';

    const note = document.getElementById(button.getAttribute('popovertarget'));
    if (!note) return;

    if (supportsPopover) {
      // Native toggle handles click, Escape and light dismiss; keep aria + position in sync
      note.addEventListener('toggle', e => {
        const opened = e.newState === 'open';
        button.setAttribute('aria-expanded', String(opened));
        if (opened) position(button, note);
      });
    } else {
      note.hidden = true;
      button.addEventListener('click', () => (isOpen(note) ? close(button, note) : open(button, note)));
      document.addEventListener('click', e => {
        if (!note.contains(e.target) && !button.contains(e.target)) close(button, note);
      });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') close(button, note); });
    }

    // Hover (mouse only): open on enter, close shortly after leaving icon and note.
    // Clicking while hover-opened pins it open instead of toggling it closed.
    let closeTimer = null;
    let openedByHover = false;
    let pinned = false;
    const cancelClose = () => clearTimeout(closeTimer);
    const scheduleClose = () => {
      cancelClose();
      if (pinned) return;
      closeTimer = setTimeout(() => close(button, note), 150);
    };

    button.addEventListener('click', e => {
      if (openedByHover && isOpen(note)) {
        e.preventDefault(); // stop the native popover toggle from closing it
        openedByHover = false;
        pinned = true;
      } else {
        pinned = !isOpen(note) || !supportsPopover ? true : pinned;
      }
    });

    note.addEventListener('toggle', e => {
      if (e.newState === 'closed') { openedByHover = false; pinned = false; }
    });

    button.addEventListener('pointerenter', e => {
      if (e.pointerType !== 'mouse') return;
      cancelClose();
      if (!isOpen(note)) {
        open(button, note);
        openedByHover = true;
      }
    });
    button.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') scheduleClose(); });
    note.addEventListener('pointerenter', cancelClose);
    note.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') scheduleClose(); });

    // Close if a scroll area scrolls sideways or the page resizes
    button.closest('[data-info-scroll]')?.addEventListener('scroll', () => close(button, note), { passive: true });
    window.addEventListener('resize', () => close(button, note), { passive: true });
    window.addEventListener('scroll', () => { if (isOpen(note)) position(button, note); }, { passive: true });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-info-note]').forEach(initInfo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => initAll(e.target));

  window.BartesianInfoNote = { initAll };
})();
